import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, ChevronUp, ChevronDown, Trash2, Plus } from "lucide-react";

const TYPES = ["text", "textarea", "date", "number", "email", "tel", "select", "checkboxes", "radio", "signature", "photo"];
const HAS_OPTIONS = ["select", "radio", "checkboxes"];

function move(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}
const keyify = (s) => (s || "field").replace(/[^a-zA-Z0-9]+/g, " ").trim().split(" ").map((p, i) => i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1)).join("") || "field";

export function TemplateEditorPanel({ clinicId = null, steps: initial = [], accent = "#0F52BA", onSaved }) {
  const [steps, setSteps] = useState(() => JSON.parse(JSON.stringify(initial || [])));
  const [saving, setSaving] = useState(false);

  const update = (fn) => setSteps((prev) => { const copy = JSON.parse(JSON.stringify(prev)); fn(copy); return copy; });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/clinic/template", { clinic_id: clinicId, steps });
      setSteps(res.data.steps);
      toast.success("Form saved.");
      onSaved?.(res.data.steps);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-slate-900">Edit form fields</h2>
          <p className="mt-1 text-sm text-slate-500">Rename labels, reorder or remove — no PDF re-upload needed.</p>
        </div>
        <Button style={{ backgroundColor: accent }} className="text-white hover:opacity-90" onClick={save} disabled={saving} data-testid="save-template-btn">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save form
        </Button>
      </div>

      <div className="mt-4 space-y-4">
        {steps.map((s, si) => (
          <div key={s.key || s.id || si} className="rounded-lg border border-slate-200" data-testid={`editor-section-${si}`}>
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-2">
              <Input value={s.title} onChange={(e) => update((c) => { c[si].title = e.target.value; })} className="h-9 font-semibold" data-testid={`section-title-${si}`} />
              <Button size="icon" variant="ghost" onClick={() => update((c) => c.splice(0, c.length, ...move(c, si, -1)))} data-testid={`section-up-${si}`}><ChevronUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => update((c) => c.splice(0, c.length, ...move(c, si, 1)))} data-testid={`section-down-${si}`}><ChevronDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500" onClick={() => update((c) => c.splice(si, 1))} data-testid={`section-del-${si}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2 p-3">
              {s.fields.map((f, fi) => (
                <div key={f.key || `${si}-${fi}`} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-100 p-2" data-testid={`editor-field-${si}-${fi}`}>
                  <Input value={f.label} onChange={(e) => update((c) => { c[si].fields[fi].label = e.target.value; })} className="h-9 flex-1 min-w-[160px]" placeholder="Field label" data-testid={`field-label-${si}-${fi}`} />
                  <Select value={f.type} onValueChange={(v) => update((c) => { c[si].fields[fi].type = v; if (HAS_OPTIONS.includes(v) && !c[si].fields[fi].options) c[si].fields[fi].options = ["Yes", "No"]; })}>
                    <SelectTrigger className="h-9 w-[130px]" data-testid={`field-type-${si}-${fi}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  {HAS_OPTIONS.includes(f.type) && (
                    <Input value={(f.options || []).join(", ")} onChange={(e) => update((c) => { c[si].fields[fi].options = e.target.value.split(",").map((x) => x.trim()).filter(Boolean); })} className="h-9 flex-1 min-w-[160px]" placeholder="Options, comma separated" data-testid={`field-options-${si}-${fi}`} />
                  )}
                  <label className="flex cursor-pointer select-none items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600" data-testid={`field-required-${si}-${fi}`}>
                    <input type="checkbox" checked={!!f.required} onChange={(e) => update((c) => { c[si].fields[fi].required = e.target.checked; })} className="h-3.5 w-3.5 accent-red-500" />
                    Required
                  </label>
                  <Button size="icon" variant="ghost" onClick={() => update((c) => c[si].fields.splice(0, c[si].fields.length, ...move(c[si].fields, fi, -1)))}><ChevronUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => update((c) => c[si].fields.splice(0, c[si].fields.length, ...move(c[si].fields, fi, 1)))}><ChevronDown className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-500" onClick={() => update((c) => c[si].fields.splice(fi, 1))} data-testid={`field-del-${si}-${fi}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => update((c) => c[si].fields.push({ key: keyify("new field " + Date.now()), label: "New field", type: "text" }))} data-testid={`add-field-${si}`}>
                <Plus className="mr-1 h-4 w-4" /> Add field
              </Button>
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={() => update((c) => c.push({ id: keyify("section " + Date.now()), key: keyify("section " + Date.now()), title: "New Section", subtitle: "", fields: [{ key: keyify("field " + Date.now()), label: "New field", type: "text" }] }))} data-testid="add-section-btn">
          <Plus className="mr-1 h-4 w-4" /> Add section
        </Button>
      </div>
    </div>
  );
}
